import { useEffect } from 'react';

const EDGE = 96;
const MAX_SPEED = 22;

function scrollParent(node) {
    let el = node?.parentElement || null;
    while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        const scrollable = /(auto|scroll)/.test(style.overflowY);
        if (scrollable && el.scrollHeight > el.clientHeight + 1) return el;
        el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
}

/**
 * While a tile is dragged, scroll the page when the pointer sits near the
 * top or bottom. Speed grows toward the edge. After the pointer leaves that
 * band, the scroll keeps going and slows down.
 */
export function useDragEdgeScroll(enabled, anchorRef) {
    useEffect(() => {
        if (!enabled || typeof document === 'undefined') return undefined;

        let velocity = 0;
        let inEdge = false;
        let dragging = false;
        let raf = 0;

        const scroller = () => scrollParent(anchorRef?.current);

        const frame = () => {
            if (velocity) {
                const node = scroller();
                if (node) node.scrollTop += velocity;
                if (!inEdge || !dragging) {
                    velocity *= 0.9;
                    if (Math.abs(velocity) < 0.4) velocity = 0;
                }
            }
            if (dragging || velocity) raf = requestAnimationFrame(frame);
            else raf = 0;
        };

        const pump = () => {
            if (!raf) raf = requestAnimationFrame(frame);
        };

        const onStart = () => {
            dragging = true;
            pump();
        };

        const onOver = (event) => {
            if (!dragging) return;
            const y = event.clientY;
            const height = window.innerHeight;
            if (y < EDGE) {
                const closeness = (EDGE - y) / EDGE;
                inEdge = true;
                velocity = -MAX_SPEED * closeness * closeness;
            } else if (y > height - EDGE) {
                const closeness = (y - (height - EDGE)) / EDGE;
                inEdge = true;
                velocity = MAX_SPEED * closeness * closeness;
            } else {
                inEdge = false;
            }
            pump();
        };

        const onEnd = () => {
            dragging = false;
            inEdge = false;
            pump();
        };

        document.addEventListener('dragstart', onStart);
        document.addEventListener('dragover', onOver);
        document.addEventListener('dragend', onEnd);
        document.addEventListener('drop', onEnd);
        return () => {
            if (raf) cancelAnimationFrame(raf);
            document.removeEventListener('dragstart', onStart);
            document.removeEventListener('dragover', onOver);
            document.removeEventListener('dragend', onEnd);
            document.removeEventListener('drop', onEnd);
        };
    }, [enabled, anchorRef]);
}
